import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, AlertTriangle, CheckCircle2, ClipboardCheck, Undo2, Target, History } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { DialogBody } from "@/components/shared/DialogBody";

/**
 * Self-contained Library Audit dialog. Auto-runs audit when opened.
 * Accepts optional `scope` prop describing current Library filters.
 */
export function AuditDialog({ open, onOpenChange, onSuccess, blocksCount = 0, scope = null, embedded = false }) {
  const [auditing, setAuditing] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [applyingAction, setApplyingAction] = useState(null);
  const [dismissedRecs, setDismissedRecs] = useState(new Set());
  const [useScope, setUseScope] = useState(true); // default: honor the user's current filters
  const [strictness, setStrictness] = useState("balanced"); // lenient | balanced | strict
  const [bulkApplying, setBulkApplying] = useState(null);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const hasActiveScope = !!scope && (scope.mode || (scope.tags && scope.tags.length > 0) || scope.stage);

  useEffect(() => {
    // Auto-run on open. In embedded (assistant) mode, run on first mount as well.
    if ((open || embedded) && !auditResults && !auditing) runAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, embedded]);

  const runAudit = async () => {
    setAuditing(true);
    setAuditResults(null);
    setDismissedRecs(new Set());
    try {
      const payload = (hasActiveScope && useScope) ? {
        mode: scope.mode || "",
        tags: scope.tags || [],
        stage: scope.stage || "",
        tag_match_all: !!scope.tagMatchAll,
        strictness,
      } : { strictness };
      const { data } = await API.post("/library/audit", payload);
      setAuditResults(data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "";
      if (detail.toLowerCase().includes("budget")) {
        toast.error("LLM budget exceeded", { description: "Go to Profile → Universal Key → Add Balance.", duration: 10000 });
      } else {
        toast.error("Audit failed", { description: detail || "Try again." });
      }
    } finally {
      setAuditing(false);
    }
  };

  const handleUndo = async (undoId, actionLabel) => {
    try {
      const { data } = await API.post(`/library/audit/undo/${undoId}`);
      toast.success(`${actionLabel} undone`, { description: `${data.restored} block${data.restored !== 1 ? "s" : ""} restored.` });
      onSuccess?.();
    } catch (e) {
      toast.error("Undo failed", { description: e.response?.data?.detail || "Snapshot may have expired." });
    }
  };

  const handleApply = async (rec, idx) => {
    setApplyingAction(idx);
    try {
      const blockIds = (rec.blocks || []).map((b) => b.id).filter(Boolean);
      if (blockIds.length === 0) { toast.error("No block IDs found"); return; }
      const payload = { action: rec.action, block_ids: blockIds, reason: rec.reason };
      if (rec.action === "optimize") payload.new_content = rec.optimized_content;
      if (rec.action === "merge") { payload.new_content = rec.merged_content; payload.new_title = rec.merged_title; }
      const { data } = await API.post("/library/audit/apply", payload);
      const actionLabel = rec.action === "delete" ? "Deleted" : rec.action === "optimize" ? "Optimized" : "Merged";
      toast.success(`${actionLabel}`, {
        description: "Applied. You can undo this within 7 days.",
        action: data.undo_id ? { label: "Undo", onClick: () => handleUndo(data.undo_id, actionLabel) } : undefined,
        duration: 8000,
      });
      setDismissedRecs((prev) => new Set([...prev, idx]));
      onSuccess?.();
    } catch (e) {
      toast.error("Failed to apply", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setApplyingAction(null);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await API.get("/library/audit/history", { params: { limit: 20 } });
      setHistory(data.entries || []);
    } catch (e) {
      toast.error("Failed to load history");
    }
  };

  const handleBulkApply = async (actionType) => {
    const recs = auditResults?.recommendations || [];
    const eligible = recs
      .map((r, idx) => ({ r, idx }))
      .filter(({ r, idx }) => r.action === actionType && !dismissedRecs.has(idx));
    if (eligible.length === 0) return;
    setBulkApplying(actionType);
    const results = [];
    const undoIds = [];
    for (const { r, idx } of eligible) {
      try {
        const blockIds = (r.blocks || []).map((b) => b.id).filter(Boolean);
        if (blockIds.length === 0) continue;
        const payload = { action: r.action, block_ids: blockIds, reason: r.reason };
        if (r.action === "optimize") payload.new_content = r.optimized_content;
        if (r.action === "merge") { payload.new_content = r.merged_content; payload.new_title = r.merged_title; }
        const { data } = await API.post("/library/audit/apply", payload);
        if (data.undo_id) undoIds.push(data.undo_id);
        results.push({ idx, ok: true });
      } catch (e) {
        results.push({ idx, ok: false });
      }
    }
    const ok = results.filter((x) => x.ok).length;
    const failed = results.length - ok;
    // Dismiss successes
    setDismissedRecs((prev) => { const n = new Set(prev); results.forEach((x) => x.ok && n.add(x.idx)); return n; });
    setBulkApplying(null);
    onSuccess?.();
    const actionVerb = actionType === "delete" ? "deleted" : actionType === "optimize" ? "optimized" : "merged";
    toast.success(`Bulk ${actionType}: ${ok} ${actionVerb}`, {
      description: failed > 0 ? `${failed} failed` : "Undo each individually from History.",
      duration: 8000,
    });
  };

  const handleClose = (next) => {
    onOpenChange(next);
    if (!next) { setApplyingAction(null); setShowHistory(false); }
  };

  const scopeLabel = (() => {
    if (!hasActiveScope || !useScope) return `All ${blocksCount} blocks`;
    const bits = [];
    if (scope.mode) bits.push(`mode=${scope.mode}`);
    if (scope.tags?.length) bits.push(`tags=${scope.tags.join(scope.tagMatchAll ? " AND " : " OR ")}`);
    if (scope.stage) bits.push(`stage=${scope.stage}`);
    return bits.join(" · ") || `All ${blocksCount} blocks`;
  })();

  const body = (
    <>
      {!embedded && (
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-bold flex items-center gap-2 pr-8 min-w-0">
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <ClipboardCheck className="w-4 h-4 text-[var(--pf-accent)] shrink-0" />
              <span className="truncate">Library Audit</span>
            </span>
            <button
              onClick={() => { setShowHistory((v) => !v); if (!history) loadHistory(); }}
              data-testid="audit-history-toggle"
              className="text-[11px] font-mono font-normal text-[var(--pf-text-muted)] hover:text-[var(--pf-accent)] flex items-center gap-1 shrink-0"
            >
              <History className="w-3 h-3" />History
            </button>
          </DialogTitle>
        </DialogHeader>
      )}
      {embedded && (
        <div className="flex items-center justify-end mb-1">
          <button
            onClick={() => { setShowHistory((v) => !v); if (!history) loadHistory(); }}
            data-testid="audit-history-toggle"
            className="text-[11px] font-mono font-normal text-[var(--pf-text-muted)] hover:text-[var(--pf-accent)] flex items-center gap-1"
          >
            <History className="w-3 h-3" />History
          </button>
        </div>
      )}

        {/* Scope selector */}
        {hasActiveScope && !showHistory && (
          <div className="flex items-center gap-2 px-3 py-2 border border-[var(--pf-border)] bg-[var(--pf-surface)] min-w-0" data-testid="audit-scope">
            <Target className="w-3.5 h-3.5 text-[var(--pf-accent)] shrink-0" />
            <span className="text-[11px] text-[var(--pf-text-secondary)] flex-1 font-mono truncate">{scopeLabel}</span>
            <button
              onClick={() => { setUseScope((v) => !v); setAuditResults(null); }}
              data-testid="audit-scope-toggle"
              className="text-[10px] font-mono text-[var(--pf-text-muted)] hover:text-[var(--pf-accent)] shrink-0"
            >
              scope: <span className={`px-1.5 py-0.5 font-semibold ml-1 ${useScope ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-bg)] border border-[var(--pf-border)] text-[var(--pf-text)]"}`}>
                {useScope ? "FILTERED" : "ALL"}
              </span>
            </button>
          </div>
        )}

        {/* Strictness selector — always shown (pre-results) */}
        {!showHistory && !auditResults && !auditing && (
          <div className="flex items-center gap-2 px-3 py-2 border border-[var(--pf-border)] bg-[var(--pf-bg)]" data-testid="audit-strictness">
            <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[var(--pf-text-muted)]">Strictness</span>
            {["lenient", "balanced", "strict"].map((s) => {
              const caps = { lenient: "≤20%", balanced: "≤40%", strict: "≤70%" };
              return (
                <button
                  key={s}
                  onClick={() => setStrictness(s)}
                  data-testid={`audit-strict-${s}`}
                  className={`text-[10px] px-2 py-1 font-medium transition-colors ${strictness === s ? "bg-[var(--pf-accent)] text-white" : "bg-[var(--pf-surface)] text-[var(--pf-text-secondary)] hover:bg-[var(--pf-accent)] hover:text-white"}`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)} <span className="opacity-60 ml-0.5">{caps[s]}</span>
                </button>
              );
            })}
            <div className="flex-1" />
            <span className="text-[10px] text-[var(--pf-text-muted)] font-mono">{strictness === "lenient" ? "Flags only clear issues" : strictness === "strict" ? "Flags anything questionable" : "Recommended"}</span>
          </div>
        )}

        {/* History panel */}
        {showHistory ? (
          <div className="space-y-2 mt-2" data-testid="audit-history-list">
            {!history ? (
              <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-[var(--pf-accent)] mx-auto" /></div>
            ) : history.length === 0 ? (
              <p className="text-xs text-[var(--pf-text-muted)] text-center py-4">No audit actions yet.</p>
            ) : (
              history.map((h, i) => (
                <div key={i} className="border border-[var(--pf-border)] px-3 py-2 text-xs min-w-0" data-testid={`history-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-[10px] px-1.5 py-0.5 font-semibold uppercase shrink-0 ${h.action === "delete" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" : h.action === "optimize" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"}`}>{h.action}</span>
                    <span className="text-[var(--pf-text-secondary)] truncate flex-1 min-w-0">{h.block_titles?.join(", ") || "(untitled)"}</span>
                    <span className="text-[10px] text-[var(--pf-text-muted)] shrink-0">{new Date(h.created_at).toLocaleDateString()}</span>
                    {h.undo_id && !h.undone && (
                      <button onClick={() => handleUndo(h.undo_id, h.action.charAt(0).toUpperCase() + h.action.slice(1))}
                        data-testid={`history-undo-${i}`}
                        className="text-[10px] text-[var(--pf-accent)] font-medium flex items-center gap-0.5 shrink-0">
                        <Undo2 className="w-3 h-3" />Undo
                      </button>
                    )}
                    {h.undone && <span className="text-[10px] text-[var(--pf-text-muted)] italic shrink-0">undone</span>}
                  </div>
                  {h.reason && <p className="text-[10px] text-[var(--pf-text-muted)] mt-1 break-words">{h.reason}</p>}
                </div>
              ))
            )}
          </div>
        ) : auditing ? (
          <div className="flex items-center gap-3 px-4 py-8" data-testid="audit-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--pf-accent)] shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--pf-text)]">Analyzing {useScope && hasActiveScope ? "filtered" : "all"} blocks...</p>
              <p className="text-[10px] text-[var(--pf-text-muted)] mt-0.5">Checking quality, finding duplicates, and identifying optimization opportunities. This takes 15-30 seconds.</p>
            </div>
          </div>
        ) : auditResults ? (
          <div className="space-y-4 mt-2" data-testid="audit-results">
            {auditResults.summary && typeof auditResults.summary === "object" && (
              <div className="grid grid-cols-5 gap-2" data-testid="audit-summary">
                <div className="border border-[var(--pf-border)] p-3 text-center"><p className="font-heading text-xl font-bold text-[#16A34A]">{auditResults.summary.strong || 0}</p><p className="text-[10px] text-[var(--pf-text-muted)]">Strong</p></div>
                <div className="border border-[var(--pf-border)] p-3 text-center"><p className="font-heading text-xl font-bold text-[var(--pf-accent)]">{auditResults.summary.needs_optimization || 0}</p><p className="text-[10px] text-[var(--pf-text-muted)]">Optimize</p></div>
                <div className="border border-[var(--pf-border)] p-3 text-center"><p className="font-heading text-xl font-bold text-amber-600">{auditResults.summary.merge_candidates || 0}</p><p className="text-[10px] text-[var(--pf-text-muted)]">Merge</p></div>
                <div className="border border-[var(--pf-border)] p-3 text-center"><p className="font-heading text-xl font-bold text-red-500">{auditResults.summary.delete_candidates || 0}</p><p className="text-[10px] text-[var(--pf-text-muted)]">Delete</p></div>
                <div className="border border-[var(--pf-border)] p-3 text-center"><p className="font-heading text-xl font-bold text-[var(--pf-text)]">{auditResults.summary.total_blocks || 0}</p><p className="text-[10px] text-[var(--pf-text-muted)]">Total</p></div>
              </div>
            )}

            {auditResults.summary?.coverage_gaps?.length > 0 && (
              <div className="px-3 py-2 border border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800" data-testid="audit-gaps">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-400 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>Coverage gaps: {auditResults.summary.coverage_gaps.join(", ")}</span>
                </p>
              </div>
            )}

            {(auditResults.recommendations || []).length === 0 ? (
              <div className="py-8 text-center">
                <CheckCircle2 className="w-8 h-8 text-[#16A34A] mx-auto mb-2" />
                <p className="text-sm font-medium text-[var(--pf-text)]">Library looks great!</p>
                <p className="text-xs text-[var(--pf-text-muted)]">No quality issues or redundancies found.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="audit-recommendations">
                {(() => {
                  const visibleRecs = (auditResults.recommendations || []).map((r, i) => ({ r, i })).filter(({ i }) => !dismissedRecs.has(i));
                  const mergeCount = visibleRecs.filter(({ r }) => r.action === "merge").length;
                  const optimizeCount = visibleRecs.filter(({ r }) => r.action === "optimize").length;
                  const deleteCount = visibleRecs.filter(({ r }) => r.action === "delete").length;
                  return (
                    <div className="flex items-center gap-2 min-w-0 flex-wrap" data-testid="audit-bulk-bar">
                      <p className="text-xs text-[var(--pf-text-secondary)] font-medium mr-2">{visibleRecs.length} recommendation{visibleRecs.length !== 1 ? "s" : ""} remaining</p>
                      <div className="flex-1" />
                      {mergeCount > 1 && (
                        <Button size="sm" variant="outline" onClick={() => handleBulkApply("merge")} disabled={!!bulkApplying} data-testid="audit-bulk-merge"
                          className="rounded-none border-amber-500 text-amber-700 dark:text-amber-400 h-7 px-2.5 text-[11px] hover:bg-amber-50 dark:hover:bg-amber-900/20">
                          {bulkApplying === "merge" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Apply all {mergeCount} merges
                        </Button>
                      )}
                      {optimizeCount > 1 && (
                        <Button size="sm" variant="outline" onClick={() => handleBulkApply("optimize")} disabled={!!bulkApplying} data-testid="audit-bulk-optimize"
                          className="rounded-none border-[var(--pf-accent)] text-[var(--pf-accent)] h-7 px-2.5 text-[11px] hover:bg-[var(--pf-accent)] hover:text-white">
                          {bulkApplying === "optimize" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Apply all {optimizeCount} optimizes
                        </Button>
                      )}
                      {deleteCount > 1 && (
                        <Button size="sm" variant="outline" onClick={() => handleBulkApply("delete")} disabled={!!bulkApplying} data-testid="audit-bulk-delete"
                          className="rounded-none border-red-500 text-red-600 dark:text-red-400 h-7 px-2.5 text-[11px] hover:bg-red-50 dark:hover:bg-red-900/20">
                          {bulkApplying === "delete" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Apply all {deleteCount} deletes
                        </Button>
                      )}
                    </div>
                  );
                })()}
                {(auditResults.recommendations || []).map((rec, idx) => {
                  if (dismissedRecs.has(idx)) return null;
                  const isApplying = applyingAction === idx;
                  const actionColor = rec.action === "optimize" ? "border-l-[var(--pf-accent)]" : rec.action === "merge" ? "border-l-amber-500" : "border-l-red-500";
                  const actionLabel = rec.action === "optimize" ? "Optimize" : rec.action === "merge" ? "Merge" : "Delete";
                  const actionBg = rec.action === "optimize" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" : rec.action === "merge" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
                  return (
                    <div key={idx} className={`border border-[var(--pf-border)] border-l-4 ${actionColor} p-3 min-w-0`} data-testid={`audit-rec-${idx}`}>
                      <div className="flex items-center gap-2 mb-2 min-w-0">
                        <span className={`text-[10px] px-1.5 py-0.5 font-semibold uppercase shrink-0 ${actionBg}`}>{actionLabel}</span>
                        <span className="text-[10px] text-[var(--pf-text-muted)] flex-1 min-w-0 break-words">{rec.reason}</span>
                      </div>
                      <div className="space-y-1 mb-2">
                        {(rec.blocks || []).map((b, bi) => (
                          <div key={bi} className="text-xs text-[var(--pf-text)] font-mono bg-[var(--pf-surface)] px-2 py-1 flex items-center gap-2 min-w-0">
                            <span className="text-[10px] px-1 py-0.5 bg-[var(--pf-bg)] text-[var(--pf-text-muted)] shrink-0">{b.mode}</span>
                            <span className="font-semibold text-[10px] uppercase tracking-[0.1em] shrink-0 truncate max-w-[40%]">{b.title}</span>
                            <span className="truncate text-[10px] text-[var(--pf-text-muted)] flex-1 min-w-0">{b.content?.substring(0, 80)}...</span>
                          </div>
                        ))}
                      </div>
                      {rec.action === "optimize" && rec.optimized_content && (
                        <div className="mb-2 min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[var(--pf-text-muted)] mb-1">Optimized version:</p>
                          <pre className="text-xs font-mono text-[var(--pf-text)] bg-[var(--pf-surface)] p-2 whitespace-pre-wrap break-words border border-[var(--pf-border)] max-w-full overflow-x-auto">{rec.optimized_content}</pre>
                        </div>
                      )}
                      {rec.action === "merge" && rec.merged_content && (
                        <div className="mb-2 min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[var(--pf-text-muted)] mb-1 break-words">Merged into: {rec.merged_title}</p>
                          <pre className="text-xs font-mono text-[var(--pf-text)] bg-[var(--pf-surface)] p-2 whitespace-pre-wrap break-words border border-[var(--pf-border)] max-w-full overflow-x-auto">{rec.merged_content}</pre>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => handleApply(rec, idx)} disabled={isApplying} data-testid={`audit-apply-${idx}`}
                          className={`rounded-none h-7 px-3 text-[11px] font-medium ${rec.action === "delete" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white"}`}>
                          {isApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : actionLabel}
                        </Button>
                        <button onClick={() => setDismissedRecs((prev) => new Set([...prev, idx]))} className="text-[11px] text-[var(--pf-text-muted)] hover:text-[var(--pf-text-secondary)]" data-testid={`audit-dismiss-${idx}`}>
                          Dismiss
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Button variant="outline" onClick={runAudit} disabled={auditing} data-testid="audit-rerun"
              className="w-full rounded-none border-[var(--pf-border)] h-8 text-xs hover:border-[var(--pf-accent)]">
              <Sparkles className="w-3 h-3 mr-1.5" />Re-audit Library
            </Button>
          </div>
        ) : null}
    </>
  );

  if (embedded) return body;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none border-[var(--pf-border)] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogBody>
          {body}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
